import React, { useState } from 'react';
import cn from 'classnames';
import { Button, Typography, useIsMounted } from 'seagull';
import { useGlobalContext } from 'seagull';
import { useStore, useStoreActions } from "../../../contexts";
import styles from "./PayoutQboSection.module.scss";
import { handleRequest, wait } from "../../../infra/utils";
import { payoutIntegrationModel, timeModel } from "../../../models";
import { getPayoutQboErrorMessage } from "../../../utils/getQboErrorMessage";
import { qboIconMap } from "../../../integrations/QBO/qboIconMap";
import ContactSupportButton from "../../../components/ContactSupportButton";
const containerSuccessStatuses = new Set(['success', 'done']);
function getQboInfoStatus(payoutIntegration) {
  switch (payoutIntegration.status) {
    case payoutIntegrationModel.Status.PendingSync:
      return 'pending';
    case payoutIntegrationModel.Status.Synced:
      return 'success';
    case payoutIntegrationModel.Status.SyncFailed:
      return 'error';
    case payoutIntegrationModel.Status.SyncInProgress:
      return 'syncing';
    default:
      return 'pending';
  }
}
function getInfo(payoutIntegration) {
  switch (payoutIntegration.status) {
    case payoutIntegrationModel.Status.PendingSync:
      {
        const invoicedReady = payoutIntegrationModel.invoicesReady(payoutIntegration);
        return invoicedReady ? 'Payout is ready to sync with QuickBooks Online.' : /*#__PURE__*/React.createElement(React.Fragment, null, "To sync the payout to QuickBooks Online, hover over the sync icon to confirm each invoice has a payment recorded and synced. Need help? ", /*#__PURE__*/React.createElement(ContactSupportButton, {
          capitalize: true
        }), ".");
      }
    case payoutIntegrationModel.Status.SyncInProgress:
      return 'Sync in progress.';
    case payoutIntegrationModel.Status.Synced:
      {
        const date = payoutIntegrationModel.date(payoutIntegration);
        return "Deposit was recorded in QuickBooks Online on ".concat(date ? timeModel.formatDateWithTime(date) : '', ".");
      }
    case payoutIntegrationModel.Status.SyncFailed:
      return getPayoutQboErrorMessage(payoutIntegration);
    default:
      return '';
  }
}
export default function PayoutQboSection(props) {
  const {
    payoutId,
    payoutIntegration,
    testid,
    reloadPayout
  } = props;
  const isMounted = useIsMounted();
  const globalContext = useGlobalContext();
  const qboPollingInterval = useStore(state => state.appConfig.qboPollingInterval);
  const {
    api
  } = useStoreActions();
  const [isSyncing, setIsSyncing] = useState(false);
  const status = getQboInfoStatus(payoutIntegration);
  const info = getInfo(payoutIntegration);
  async function syncPayout() {
    setIsSyncing(true);
    const {
      success
    } = await handleRequest(api.qbo.syncPayout(payoutId));
    if (!isMounted()) return;
    if (!success) {
      openSyncToast('failure');
    } else {
      await wait(qboPollingInterval);
      const newPayout = await reloadPayout();
      if (!isMounted()) return;
      newPayout.integration && openSyncToast(payoutIntegrationModel.isSyncFailed(newPayout.integration) ? 'failure' : 'success');
    }
    setIsSyncing(false);
  }
  function openSyncToast(type) {
    if (type === 'success') {
      globalContext.showSuccessToast('Payout was successfully synced', 'sync-success-toast');
    } else {
      globalContext.showErrorToast('Failed to sync payout', 'sync-failure-toast');
    }
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cn(styles.container, {
      [styles.success]: containerSuccessStatuses.has(status),
      [styles.error]: status === 'error'
    }),
    "data-testid": testid,
    "data-status": status
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.status
  }, /*#__PURE__*/React.createElement("div", null, status && qboIconMap[status]), /*#__PURE__*/React.createElement(Typography, {
    "data-testid": testid && "".concat(testid, "-info")
  }, info)), /*#__PURE__*/React.createElement("div", {
    className: styles.actions
  }, payoutIntegrationModel.isSyncFailed(payoutIntegration) && /*#__PURE__*/React.createElement(Button, {
    className: styles.retrySyncBtn,
    skin: "primaryOutline",
    size: "small",
    "data-testid": "retry-sync-payout",
    loading: isSyncing,
    loadingText: "Syncing...",
    onClick: () => syncPayout()
  }, "Retry sync"), payoutIntegrationModel.invoicesReady(payoutIntegration) && /*#__PURE__*/React.createElement(Button, {
    className: styles.actionButton,
    "data-testid": "sync-payout",
    onClick: () => syncPayout(),
    skin: "primaryOutline",
    size: "small",
    loading: isSyncing,
    loadingText: "Syncing..."
  }, "Sync")));
}